import {
	type PoListExportsData,
	type PoListProposalsData,
	type PoListRunsData,
	poGetConfig,
	poGetProposalHistory,
	poGetProposals,
	poGetRuns,
	poGetSystemStatus,
	poListDistributors,
	poListExportAuthors,
	poListExports,
	poListProposals,
	poListRuns
} from '@repo/sdk/gen'
import { queryOptions } from '@tanstack/react-query'
import { makeQueryKeys } from '@/lib/query-keys'

export function runsListQueryOptions(body: PoListRunsData['body']) {
	return queryOptions({
		queryKey: makeQueryKeys.po.runsIndex(body),
		queryFn: (queryContext) =>
			poListRuns({ body, signal: queryContext.signal }),
		placeholderData: (prev) => prev,
		// Returning to the list must keep an active generation moving through
		// its states, just like staying on the document detail does.
		refetchInterval: (query) =>
			query.state.data?.data.some(
				(run) => run.status === 'pending' || run.status === 'running'
			)
				? 3000
				: false
	})
}

export function runDetailQueryOptions(runId: string) {
	return queryOptions({
		queryKey: makeQueryKeys.po.runDetail(runId),
		queryFn: async ({ signal }) => {
			const response = await poGetRuns({
				body: { filters: { ids: [runId] } },
				signal
			})
			return response.data[0] ?? null
		},
		// while a generation is running the document must refresh by itself
		refetchInterval: (query) => {
			const status = query.state.data?.status
			return status === 'pending' || status === 'running' ? 3000 : false
		}
	})
}

export function proposalsListQueryOptions(body: PoListProposalsData['body']) {
	return queryOptions({
		queryKey: makeQueryKeys.po.proposalsIndex(body.filters.runId, body),
		queryFn: (queryContext) =>
			poListProposals({ body, signal: queryContext.signal }),
		placeholderData: (prev) => prev
	})
}

export function proposalDetailQueryOptions(id: string) {
	return queryOptions({
		queryKey: makeQueryKeys.po.proposalDetail(id),
		queryFn: async ({ signal }) => {
			const response = await poGetProposals({
				body: { filters: { ids: [id] } },
				signal
			})
			return response.data[0] ?? null
		}
	})
}

export function proposalHistoryQueryOptions(id: string) {
	return queryOptions({
		queryKey: makeQueryKeys.po.proposalHistory(id),
		queryFn: async ({ signal }) => {
			const response = await poGetProposalHistory({
				body: { filters: { id } },
				signal
			})
			return response.data
		}
	})
}

export function distributorsQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.po.distributors(),
		queryFn: async ({ signal }) => {
			const response = await poListDistributors({ signal })
			return response.data
		}
	})
}

export function poExportsListQueryOptions(body: PoListExportsData['body']) {
	return queryOptions({
		queryKey: makeQueryKeys.po.exportsIndex(body),
		queryFn: (queryContext) =>
			poListExports({ body, signal: queryContext.signal }),
		placeholderData: (prev) => prev,
		// exports in generation complete within seconds — keep the list live
		refetchInterval: (query) =>
			query.state.data?.data.some((e) => e.status === 'pending')
				? 2500
				: false
	})
}

export function poExportAuthorsQueryOptions(runId?: string) {
	return queryOptions({
		queryKey: makeQueryKeys.po.exportAuthors(runId),
		queryFn: async ({ signal }) => {
			const response = await poListExportAuthors({
				body: { filters: { runId } },
				signal
			})
			return response.data
		}
	})
}

export function systemStatusQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.po.system(),
		queryFn: async ({ signal }) => {
			const response = await poGetSystemStatus({ signal })
			return response.data
		},
		refetchInterval: 30_000
	})
}

export function poConfigQueryOptions(includeHistory = false) {
	return queryOptions({
		queryKey: [...makeQueryKeys.po.config(), includeHistory] as const,
		queryFn: async ({ signal }) => {
			const response = await poGetConfig({
				body: { includeHistory },
				signal
			})
			return response.data
		}
	})
}
